import { TextField, Box, Typography } from '@mui/material';
import type { ParameterDefinition, LinkedEntity } from '@/graph/domain/types';
import {
  UnitValueField, type UnitValue,
  DropdownField,
  GradientProgramField, type GradientProgramValue,
  TableParameterField, type TableData,
  GroupField,
} from '@/components/fields';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Validates and filters a string value based on the field type.
 * For 'text' type: rejects numeric characters
 * For 'number' type: rejects alphabetic characters (handled by HTML input type)
 */
function validateParameterInput(value: string, fieldType: string): string {
  if (fieldType === 'text') {
    // For text-only fields, remove all numeric digits
    return value.replace(/\d/g, '');
  }
  // For other types, return as-is (number type is handled by HTML input)
  return value;
}

/**
 * Validates a UnitValue object, filtering the amount based on field type
 */
function validateUnitValue(value: UnitValue, fieldType: string): UnitValue {
  if (fieldType === 'text') {
    // For text-only fields, filter digits from amount
    const amount = String(value.amount || '').replace(/\d/g, '');
    return { ...value, amount };
  }
  return value;
}

/**
 * Validates a range value to ensure it's within min/max bounds.
 * Returns the value if valid, or empty string if invalid.
 */
function validateRangeInput(
  value: string | number | null,
  min: number | null | undefined,
  max: number | null | undefined,
): string | number | null {
  if (value === null || value === '' || value === undefined) return '';
  
  const numValue = typeof value === 'string' ? Number(value) : value;
  
  // If not a valid number, reject
  if (!Number.isFinite(numValue)) return '';
  
  // Check bounds
  if (min !== null && min !== undefined && numValue < min) return '';
  if (max !== null && max !== undefined && numValue > max) return '';
  
  return numValue;
}

function hasValidUnits(tags?: string[]): boolean {
  if (!tags || tags.length === 0) return false;
  return tags.some((u) => u && u !== 'none' && u.trim() !== '');
}

function extractPrimitive(raw: unknown): string | number {
  if (raw == null) return '';
  if (typeof raw === 'string' || typeof raw === 'number') return raw;
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (obj.label != null && obj.label !== '') return String(obj.label);
    if (obj.value != null && obj.value !== '') return extractPrimitive(obj.value);
    if (obj.amount != null && obj.amount !== '') return extractPrimitive(obj.amount);
    if (obj.input != null && obj.input !== '') return extractPrimitive(obj.input);
  }
  return '';
}

function unwrapValueWrapper(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const obj = raw as Record<string, unknown>;
  const isParameterWrapper = ('parameterId' in obj || 'parameterName' in obj) && 'value' in obj;
  if (isParameterWrapper) return obj.value;
  return raw;
}

function toScalarText(raw: unknown): string {
  const unwrapped = unwrapValueWrapper(raw);
  return String(extractPrimitive(unwrapped));
}

function toScalarNumber(raw: unknown): number | '' {
  const unwrapped = unwrapValueWrapper(raw);
  const primitive = extractPrimitive(unwrapped);
  if (primitive === '' || primitive == null) return '';
  const n = typeof primitive === 'number' ? primitive : Number(primitive);
  return Number.isFinite(n) ? n : '';
}

/** Normalise a stored value into the UnitValue shape expected by UnitValueField. */
function toUnitValue(raw: unknown, fallbackUnit: string): UnitValue {
  const unwrapped = unwrapValueWrapper(raw);
  if (unwrapped && typeof unwrapped === 'object') {
    const obj = unwrapped as Record<string, unknown>;
    const nested =
      (obj.amount && typeof obj.amount === 'object' ? (obj.amount as Record<string, unknown>) : null) ??
      (obj.value && typeof obj.value === 'object' ? (obj.value as Record<string, unknown>) : null);

    if (nested) {
      return {
        amount: (nested.amount ?? nested.value ?? '') as string | number | null,
        unit: (nested.unit ?? nested.units ?? obj.unit ?? obj.units ?? fallbackUnit) as string,
      };
    }

    return {
      amount: (extractPrimitive(obj.amount ?? obj.value ?? '') as string | number | null),
      unit: (obj.unit ?? obj.units ?? fallbackUnit) as string,
    };
  }
  return { amount: extractPrimitive(unwrapped) as string | number, unit: fallbackUnit };
}

/** Extract a single numeric value from raw data for range fields. */
function getRangeScalarValue(raw: unknown): number | string {
  const unwrapped = unwrapValueWrapper(raw);
  const primitive = extractPrimitive(unwrapped);
  if (primitive === '' || primitive == null) return '';
  const n = typeof primitive === 'number' ? primitive : Number(primitive);
  return Number.isFinite(n) ? n : '';
}

function objectMapToRows(raw: Record<string, unknown>): Array<Record<string, unknown>> {
  const numericKeys = Object.keys(raw).filter((k) => /^\d+$/.test(k));
  if (numericKeys.length === 0) return [];
  return numericKeys
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => raw[k])
    .filter((v): v is Record<string, unknown> => Boolean(v) && typeof v === 'object' && !Array.isArray(v));
}

function pickRows(raw: unknown, depth = 0): Array<Record<string, unknown>> {
  if (depth > 5) return [];
  if (Array.isArray(raw)) return raw as Array<Record<string, unknown>>;
  if (!raw || typeof raw !== 'object') return [];

  const obj = raw as Record<string, unknown>;
  const directKeys = ['tableRows', 'gradientRows', 'GradientProgram', 'rows', 'data'];
  for (const key of directKeys) {
    const val = obj[key];
    if (Array.isArray(val)) return val as Array<Record<string, unknown>>;
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const mapped = objectMapToRows(val as Record<string, unknown>);
      if (mapped.length > 0) return mapped;
    }
  }

  const nestedKeys = ['value', 'parameters', 'payload', 'result', 'data'];
  for (const key of nestedKeys) {
    const nested = obj[key];
    if (nested && typeof nested === 'object') {
      const rows = pickRows(nested, depth + 1);
      if (rows.length > 0) return rows;
    }
  }

  return objectMapToRows(obj);
}

function toGradientValue(raw: unknown, defaultPumpCount = 2): GradientProgramValue | null {
  const unwrapped = unwrapValueWrapper(raw);
  const rows = pickRows(unwrapped);

  let totalPumps = defaultPumpCount;
  if (unwrapped && typeof unwrapped === 'object') {
    const obj = unwrapped as Record<string, unknown>;
    const directPumps = typeof obj.totalPumps === 'number' ? obj.totalPumps : undefined;
    const nestedObj = obj.value && typeof obj.value === 'object' ? (obj.value as Record<string, unknown>) : undefined;
    const nestedPumps = nestedObj && typeof nestedObj.totalPumps === 'number' ? nestedObj.totalPumps : undefined;
    totalPumps = (nestedPumps ?? directPumps ?? defaultPumpCount);
  }

  const gradientRows = (rows.length > 0 ? rows : []).map((row) => {
    const mapped: Record<string, string> = {};
    for (const [k, v] of Object.entries(row)) mapped[k] = String(extractPrimitive(v));
    return mapped as any;
  });

  return {
    totalPumps: totalPumps > 0 ? totalPumps : defaultPumpCount,
    gradientRows,
  };
}

function toTableData(raw: unknown): TableData | null {
  const unwrapped = unwrapValueWrapper(raw);
  const rows = pickRows(unwrapped);
  const tableRows = rows.map((row) => {
    const mapped: Record<string, string> = {};
    for (const [k, v] of Object.entries(row)) mapped[k] = String(extractPrimitive(v));
    return mapped;
  });
  return { tableRows };
}

function toGroupValue(raw: unknown, allowMultiple: boolean): Record<string, unknown> | Record<string, unknown>[] {
  const unwrapped = unwrapValueWrapper(raw);
  if (allowMultiple) {
    const rows = pickRows(unwrapped);
    if (rows.length > 0) return rows;
    if (Array.isArray(unwrapped)) return unwrapped as Record<string, unknown>[];
    if (unwrapped && typeof unwrapped === 'object') return [unwrapped as Record<string, unknown>];
    return [];
  }
  if (unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped)) {
    return unwrapped as Record<string, unknown>;
  }
  return {};
}

function normalizeTableColumns(
  columns: Array<{ id: string; header: string; required?: boolean }> = [],
): Array<{ id: string; header: string; required?: boolean }> {
  const used = new Set<string>();
  return columns.map((c, idx) => {
    const base = String(c.id || c.header || `col_${idx + 1}`).trim() || `col_${idx + 1}`;
    let key = base;
    let n = 2;
    while (used.has(key)) {
      key = `${base}_${n}`;
      n += 1;
    }
    used.add(key);
    return {
      id: key,
      header: String(c.header || c.id || `Column ${idx + 1}`),
      required: c.required,
    };
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ParameterFieldProps {
  definition: ParameterDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  focusedFieldPath?: string | null;
  uniquenessError?: string;
  uniquenessPending?: boolean;
  uniquenessErrors?: Record<string, string>;
  uniquenessPendingMap?: Record<string, boolean>;
  linkedEntities?: LinkedEntity[];
  onLinkedEntitiesChange?: (entities: LinkedEntity[]) => void;
  onOpenModal?: (config: any, callback: (result: any) => void) => void;
}

/**
 * Wrapper component to display label above input field
 */
function FieldWrapper({
  children,
  label,
  required,
  error,
  helperText,
}: {
  children: React.ReactNode;
  label: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}) {
  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          mb: 0.75,
          fontWeight: 500,
          color: error ? 'error.main' : 'text.primary',
        }}
      >
        {label}
        {required && (
          <Typography component="span" sx={{ color: 'error.main', ml: 0.25 }}>
            *
          </Typography>
        )}
      </Typography>
      {children}
      {helperText && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            display: 'block',
            color: error ? 'error.main' : 'text.secondary',
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

export function ParameterField({
  definition,
  value,
  onChange,
  focusedFieldPath,
  uniquenessError,
  uniquenessPending,
  uniquenessErrors = {},
  uniquenessPendingMap = {},
}: ParameterFieldProps) {
  const label = definition.parameterName;
  const required = definition.required;
  const tags = definition.parameterTags ?? [];
  const validUnits = hasValidUnits(tags);
  const hintText = uniquenessError ?? (uniquenessPending ? 'Checking uniqueness...' : '');

  switch (definition.fieldType) {
    // ── Plain text ─────────────────────────────────────────────────────────
    case 'text':
      if (validUnits) {
        return (
          <FieldWrapper
            label={label}
            required={required}
            error={Boolean(uniquenessError)}
            helperText={hintText}
          >
            <UnitValueField
              label=""
              required={false}
              value={toUnitValue(value,  '')}
              units={tags}
              inputType="text"
              placeholder={`Enter ${label}`}
              onChange={(unitValue: UnitValue) => {
                const validated = validateUnitValue(unitValue, 'text');
                onChange(validated);
              }}
              data-testid={`param-${definition.id}`}
            />
          </FieldWrapper>
        );
      }
      return (
        <FieldWrapper
          label={label}
          required={required}
          error={Boolean(uniquenessError)}
          helperText={hintText}
        >
          <TextField
            size="small"
            placeholder={`Enter ${label}`}
            fullWidth
            value={toScalarText(value)}
            onChange={(e) => {
              const validated = validateParameterInput(e.target.value, 'text');
              onChange(validated);
            }}
            data-testid={`param-${definition.id}`}
          />
        </FieldWrapper>
      );

    // ── Alphanumeric text — with or without units ───────────────────────────
    case 'textNumber':
      if (validUnits) {
        return (
          <FieldWrapper
            label={label}
            required={required}
            error={Boolean(uniquenessError)}
            helperText={hintText}
          >
            <UnitValueField
              label=""
              required={false}
              value={toUnitValue(value, '')}
              units={tags}
              inputType="text"
              placeholder={`Enter ${label}`}
              onChange={onChange as (v: UnitValue) => void}
              data-testid={`param-${definition.id}`}
            />
          </FieldWrapper>
        );
      }
      return (
        <FieldWrapper
          label={label}
          required={required}
          error={Boolean(uniquenessError)}
          helperText={hintText}
        >
          <TextField
            size="small"
            fullWidth
            value={toScalarText(value)}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`param-${definition.id}`}
            slotProps={{ input: { placeholder: `Enter ${label}` } }}
          />
        </FieldWrapper>
      );

    // ── Numeric — with or without units ────────────────────────────────────
    case 'number':
      if (validUnits) {
        return (
          <FieldWrapper
            label={label}
            required={required}
            error={Boolean(uniquenessError)}
            helperText={hintText}
          >
            <UnitValueField
              label=""
              required={false}
              value={toUnitValue(value, '')}
              units={tags}
              inputType="number"
              placeholder={`Enter ${label}`}
              onChange={onChange as (v: UnitValue) => void}
              data-testid={`param-${definition.id}`}
            />
          </FieldWrapper>
        );
      }
      return (
        <FieldWrapper
          label={label}
          required={required}
          error={Boolean(uniquenessError)}
          helperText={hintText}
        >
          <TextField
            size="small"
            type="number"
            fullWidth
            value={toScalarNumber(value)}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
            data-testid={`param-${definition.id}`}
            slotProps={{ 
              input: { placeholder: `Enter ${label}` },
              htmlInput: { step: 'any' }
            }}
          />
        </FieldWrapper>
      );

    // ── Amount + unit ───────────────────────────────────────────────────────
    case 'units':
      return (
        <FieldWrapper
          label={label}
          required={required}
          error={Boolean(uniquenessError)}
          helperText={hintText}
        >
          <UnitValueField
            label=""
            required={false}
            value={toUnitValue(value,  '')}
            units={tags}
            inputType="text"
            placeholder={`Enter ${label}`}
            onChange={(unitValue: UnitValue) => {
              onChange(unitValue);
            }}
            data-testid={`param-${definition.id}`}
          />
        </FieldWrapper>
      );

    // ── Dropdown ────────────────────────────────────────────────────────────
    case 'dropdown':
      return (
        <FieldWrapper
          label={label}
          required={required}
          error={Boolean(uniquenessError)}
          helperText={hintText}
        >
          <DropdownField
            label=""
            required={false}
            value={unwrapValueWrapper(value)}
            options={definition.dropDownList ?? []}
            placeholder={`Select ${label}`}
            onChange={onChange as (v: string) => void}
            data-testid={`param-${definition.id}`}
          />
        </FieldWrapper>
      );

    // ── Min / Max range ─────────────────────────────────────────────────────
    case 'range': {
      const min = definition.rangeMin;
      const max = definition.rangeMax;
      
      // Build label with range brackets for reference
      const rangeBrackets = (min !== null && min !== undefined && max !== null && max !== undefined)
        ? ` (${min} - ${max})`
        : '';
      const fullLabel = label + rangeBrackets;
      
      if (validUnits) {
        return (
          <FieldWrapper
            label={fullLabel}
            required={required}
            error={Boolean(uniquenessError)}
            helperText={hintText}
          >
            <UnitValueField
              label=""
              required={false}
              value={toUnitValue(value,  '')}
              units={tags}
              inputType="number"
              placeholder={`Enter ${label}`}
              onChange={onChange as (v: UnitValue) => void}
              data-testid={`param-${definition.id}`}
            />
          </FieldWrapper>
        );
      }
      
      const currentVal = getRangeScalarValue(value);
      return (
        <FieldWrapper
          label={fullLabel}
          required={required}
          error={Boolean(uniquenessError)}
          helperText={hintText}
        >
          <TextField
            size="small"
            type="number"
            fullWidth
            value={currentVal === '' ? '' : currentVal}
            onChange={(e) => {
              const validated = validateRangeInput(e.target.value, min, max);
              if (validated !== '') {
                onChange(validated);
              }
            }}
            data-testid={`param-${definition.id}`}
            slotProps={{ 
              input: { placeholder: `Enter ${label}` },
              htmlInput: { step: 'any' }
            }}
          />
        </FieldWrapper>
      );
    }

    // ── Date ────────────────────────────────────────────────────────────────
    case 'date':
      return (
        <FieldWrapper
          label={label}
          required={required}
          error={Boolean(uniquenessError)}
          helperText={hintText}
        >
          <TextField
            size="small"
            type="date"
            fullWidth
            value={toScalarText(value)}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`param-${definition.id}`}
            slotProps={{ 
              inputLabel: { shrink: true },
              input: { placeholder: `Enter ${label}` }
            }}
          />
        </FieldWrapper>
      );

    // ── Group (single or multi-row) ─────────────────────────────────────────
    case 'group': {
      const children = [...(definition.children ?? [])].sort((a, b) => a.order - b.order);
      return (
        <FieldWrapper
          label={label}
          required={required}
          error={Boolean(uniquenessError)}
          helperText={hintText}
        >
          <Box data-testid={`param-group-${definition.id}`}>
            <GroupField
              label=""
              required={false}
              childDefinitions={children}
              value={toGroupValue(value, definition.allowMultiple ?? false)}
              allowMultiple={definition.allowMultiple ?? false}
              onChange={onChange as (v: unknown) => void}
              pathPrefix={definition.parameterName}
              focusedPath={focusedFieldPath}
              uniquenessErrors={uniquenessErrors}
              uniquenessPendingMap={uniquenessPendingMap}
            />
          </Box>
        </FieldWrapper>
      );
    }

    // ── Gradient program ────────────────────────────────────────────────────
    case 'gradientProgram':
      return (
        <FieldWrapper
          label={label}
          required={required}
          error={Boolean(uniquenessError)}
          helperText={hintText}
        >
          <GradientProgramField
            label=""
            required={false}
            value={toGradientValue(value, definition.pumpCount ?? 2)}
            defaultPumpCount={definition.pumpCount ?? 2}
            placeholder={`Enter ${label}`}
            onChange={onChange as (v: GradientProgramValue) => void}
            data-testid={`param-${definition.id}`}
          />
        </FieldWrapper>
      );

    // ── Table ───────────────────────────────────────────────────────────────
    case 'table':
      return (
        <FieldWrapper
          label={label}
          required={required}
          error={Boolean(uniquenessError)}
          helperText={hintText}
        >
          <TableParameterField
            label=""
            required={false}
            columns={normalizeTableColumns((definition.tableColumns ?? []).map((c: { id: string; header: string; required?: boolean }) => ({
              id: c.id,
              header: c.header,
              required: c.required,
            })))}
            value={toTableData(value)}
            placeholder={`Enter ${label}`}
            onChange={onChange as (v: TableData) => void}
            readonly={false}
            data-testid={`param-${definition.id}`}
          />
        </FieldWrapper>
      );

    // ── User / file / linkEntity — simple text fallback ─────────────────────
    case 'user':
    case 'file':
      return (
        <FieldWrapper
          label={label}
          required={required}
          error={Boolean(uniquenessError)}
          helperText={hintText}
        >
          <TextField
            size="small"
            fullWidth
            value={toScalarText(value)}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`param-${definition.id}`}
            slotProps={{ input: { placeholder: `Enter ${label}` } }}
          />
        </FieldWrapper>
      );

    case 'linkEntity':
      return (
        <FieldWrapper
          label={label}
          required={required}
          error={Boolean(uniquenessError)}
          helperText={hintText}
        >
          <TextField
            size="small"
            fullWidth
            value={toScalarText(value)}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`param-${definition.id}`}
            slotProps={{ input: { placeholder: `Enter ${label}` } }}
          />
        </FieldWrapper>
      );

    default:
      return null;
  }
}
